import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material'
import {
  Assignment,
  Timeline,
  CheckCircle,
  Error,
  Schedule,
  Person,
  ExpandMore,
  Add,
  Edit,
  Visibility,
  TrendingUp,
  Warning,
  Assessment,
  Refresh,
  Autorenew,
  TouchApp,
  Sync,
  AdminPanelSettings
} from '@mui/icons-material'
import axios from 'axios'
import { format, isAfter, isBefore, addDays } from 'date-fns'
import { es } from 'date-fns/locale'

const VerificacionObjetivos = () => {
  const { user, isAdmin } = useAuth()
  const [objetivos, setObjetivos] = useState([])
  const [verificaciones, setVerificaciones] = useState({})
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedObjetivo, setSelectedObjetivo] = useState(null)
  const [verificacionDialog, setVerificacionDialog] = useState(false)
  const [detalleDialog, setDetalleDialog] = useState(false)
  const [filters, setFilters] = useState({
    actor_tipo: '',
    estado: '',
    gestion_id: ''
  })
  
  // Formulario de verificación
  const [verificacionForm, setVerificacionForm] = useState({
    progreso_reportado: 0,
    cumplimiento_calidad: 'regular',
    observaciones_verificacion: '',
    acciones_requeridas: '',
    siguiente_revision: '',
    aprobado: false
  })

  useEffect(() => {
    loadData()
  }, [filters, user])

  const loadData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')
      
      // Construir parámetros de filtro
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      
      // Cargar objetivos
      const objetivosResponse = await axios.get(`/api/auditoria/objetivos?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setObjetivos(objetivosResponse.data.objetivos || [])
      
      // Cargar estadísticas
      const statsResponse = await axios.get('/api/auditoria/objetivos/stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setStats(statsResponse.data)
      
    } catch (error) {
      setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const loadVerificaciones = async (objetivoId) => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/auditoria/objetivos/${objetivoId}/verificaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setVerificaciones(prev => ({
        ...prev,
        [objetivoId]: response.data.verificaciones
      }))
    } catch (error) {
      console.error('Error al cargar verificaciones:', error)
    }
  }

  const handleVerObjetivo = async (objetivo) => {
    setSelectedObjetivo(objetivo)
    await loadVerificaciones(objetivo.id)
    setDetalleDialog(true)
  }

  const handleCrearVerificacion = (objetivo) => {
    setSelectedObjetivo(objetivo)
    setVerificacionForm({
      progreso_reportado: objetivo.progreso_porcentaje || 0,
      cumplimiento_calidad: 'regular',
      observaciones_verificacion: '',
      acciones_requeridas: '',
      siguiente_revision: '',
      aprobado: false
    })
    setVerificacionDialog(true)
  }

  const handleSubmitVerificacion = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      
      await axios.post(
        `/api/auditoria/objetivos/${selectedObjetivo.id}/verificaciones`,
        verificacionForm,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      setVerificacionDialog(false)
      await loadData() // Recargar datos
      
      // Recargar verificaciones si el detalle está abierto
      if (detalleDialog) {
        await loadVerificaciones(selectedObjetivo.id)
      }
      
    } catch (error) {
      setError('Error al crear verificación: ' + (error.response?.data?.detail || error.message))
    }
  }

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'completado': return 'success'
      case 'en_progreso': return 'info'
      case 'pendiente': return 'warning'
      case 'vencido': return 'error'
      case 'cancelado': return 'default'
      default: return 'default'
    }
  }

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'completado': return <CheckCircle />
      case 'en_progreso': return <Timeline />
      case 'pendiente': return <Schedule />
      case 'vencido': return <Error />
      default: return <Assignment />
    }
  }

  const getCumplimientoColor = (cumplimiento) => {
    switch (cumplimiento) {
      case 'excelente': return 'success'
      case 'bueno': return 'info'
      case 'regular': return 'warning'
      case 'deficiente': return 'error'
      default: return 'default'
    }
  }

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A'
    return format(new Date(fecha), 'dd/MM/yyyy', { locale: es })
  }

  const isProximoVencimiento = (fechaLimite) => {
    if (!fechaLimite) return false
    const limite = new Date(fechaLimite)
    const hoy = new Date()
    const en15Dias = addDays(hoy, 15)
    
    return isAfter(limite, hoy) && isBefore(limite, en15Dias)
  }

  const puedeCrearVerificacion = (objetivo) => {
    if (!user) return false

    return (
      isAdmin ||
      (objetivo.supervisor && objetivo.supervisor.email === user.email)
    )
  }

  const handleActualizarProgreso = async (objetivoId, tipoSeguimiento) => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.post(
        `/api/auditoria/objetivos/${objetivoId}/actualizar-progreso`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      // Mostrar resultado de la actualización
      const resultado = response.data
      alert(`Progreso actualizado (${tipoSeguimiento}): ${resultado.cantidad_actual}/${resultado.meta_numero} (${resultado.progreso_porcentaje}%)`)
      
      // Recargar datos
      await loadData()
      
    } catch (error) {
      setError('Error al actualizar progreso: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleActualizarTodosAutomaticos = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.post(
        '/api/auditoria/objetivos/actualizar-todos-automaticos',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      const resultado = response.data
      alert(resultado.message)
      
      // Recargar datos
      await loadData()
      
    } catch (error) {
      setError('Error al actualizar objetivos automáticos: ' + (error.response?.data?.detail || error.message))
    }
  }

  const getTipoSeguimientoIcon = (tipo) => {
    return tipo === 'automatico' ? <Autorenew /> : <TouchApp />
  }

  const getTipoSeguimientoColor = (tipo) => {
    return tipo === 'automatico' ? 'success' : 'info'
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando verificación de objetivos...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assignment sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Verificación de Objetivos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadData}>
            <Refresh />
          </IconButton>
          {isAdmin && (
            <>
              <Tooltip title="Actualizar todos los objetivos automáticos">
                <IconButton onClick={handleActualizarTodosAutomaticos} color="primary">
                  <Sync />
                </IconButton>
              </Tooltip>
              <Tooltip title="Administrar objetivos (CRUD)">
                <Button 
                  variant="outlined" 
                  startIcon={<AdminPanelSettings />}
                  onClick={() => {
                    // Cambiar a la pestaña de administración
                    const auditoriaViewer = document.querySelector('[aria-controls="auditoria-tabpanel-2"]')
                    if (auditoriaViewer) {
                      auditoriaViewer.click()
                    }
                  }}
                  size="small"
                >
                  Admin
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {stats.stats_por_actor.map((stat) => (
            <Grid item xs={12} md={4} key={stat.actor_tipo}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <Person />
                    </Avatar>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {stat.actor_tipo}s
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                      <Typography variant="h6">{stat.total_objetivos}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Progreso Promedio</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={stat.progreso_promedio} 
                          sx={{ flex: 1 }}
                        />
                        <Typography variant="body2">
                          {Math.round(stat.progreso_promedio)}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Completados: ${stat.completados}`} color="success" size="small" />
                    <Chip label={`En progreso: ${stat.en_progreso}`} color="info" size="small" />
                    <Chip label={`Pendientes: ${stat.pendientes}`} color="warning" size="small" />
                    {stat.vencidos > 0 && (
                      <Chip label={`Vencidos: ${stat.vencidos}`} color="error" size="small" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Objetivos próximos a vencer */}
      {stats && stats.proximos_a_vencer.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Objetivos próximos a vencer (15 días):
          </Typography>
          <List dense>
            {stats.proximos_a_vencer.map((objetivo) => (
              <ListItem key={objetivo.id} dense>
                <ListItemText
                  primary={objetivo.objetivo_descripcion}
                  secondary={`${objetivo.responsable_nombre} - Vence: ${formatFecha(objetivo.fecha_limite)} - ${objetivo.progreso_porcentaje}%`}
                />
                <Badge badgeContent={<Warning />} color="error" />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Actor</InputLabel>
              <Select
                value={filters.actor_tipo}
                label="Tipo de Actor"
                onChange={(e) => setFilters({...filters, actor_tipo: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="administrador">Administrador</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="consultor">Consultor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.estado}
                label="Estado"
                onChange={(e) => setFilters({...filters, estado: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="en_progreso">En Progreso</MenuItem>
                <MenuItem value="completado">Completado</MenuItem>
                <MenuItem value="vencido">Vencido</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de objetivos */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Objetivo</TableCell>
              <TableCell>Actor/Responsable</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Tipo Seguimiento</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Progreso</TableCell>
              <TableCell>Fecha Límite</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {objetivos.map((objetivo) => (
              <TableRow 
                key={objetivo.id} 
                hover
                sx={{
                  bgcolor: isProximoVencimiento(objetivo.fecha_limite) ? 'warning.light' : 'inherit',
                  opacity: objetivo.estado === 'completado' ? 0.8 : 1
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {objetivo.objetivo_descripcion}
                    </Typography>
                    {objetivo.meta_cuantitativa && (
                      <Typography variant="caption" color="text.secondary">
                        Meta: {objetivo.meta_cuantitativa}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Chip 
                      label={objetivo.actor_tipo} 
                      color="primary" 
                      variant="outlined" 
                      size="small" 
                      sx={{ mb: 0.5, textTransform: 'capitalize' }}
                    />
                    <Typography variant="body2">
                      {objetivo.responsable.nombre}
                    </Typography>
                    {objetivo.gestion_nombre && (
                      <Typography variant="caption" color="text.secondary">
                        {objetivo.gestion_nombre}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Chip 
                    label={objetivo.categoria} 
                    color="secondary" 
                    variant="outlined" 
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                
                <TableCell>
                  <Chip
                    icon={getTipoSeguimientoIcon(objetivo.tipo_seguimiento)}
                    label={objetivo.tipo_seguimiento === 'automatico' ? 'Automático' : 'Manual'}
                    color={getTipoSeguimientoColor(objetivo.tipo_seguimiento)}
                    size="small"
                    variant="outlined"
                  />
                  {objetivo.tabla_seguimiento && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {objetivo.tabla_seguimiento}
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell>
                  <Chip
                    icon={getEstadoIcon(objetivo.estado)}
                    label={objetivo.estado}
                    color={getEstadoColor(objetivo.estado)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  {isProximoVencimiento(objetivo.fecha_limite) && (
                    <Tooltip title="Próximo a vencer">
                      <Warning color="warning" fontSize="small" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={objetivo.progreso_porcentaje} 
                      sx={{ flex: 1, minWidth: 60 }}
                    />
                    <Typography variant="body2">
                      {objetivo.progreso_porcentaje}%
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color={isProximoVencimiento(objetivo.fecha_limite) ? 'warning.main' : 'text.primary'}
                    fontWeight={isProximoVencimiento(objetivo.fecha_limite) ? 'bold' : 'normal'}
                  >
                    {formatFecha(objetivo.fecha_limite)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {objetivo.periodo_evaluacion}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" onClick={() => handleVerObjetivo(objetivo)}>
                      <Visibility />
                    </IconButton>
                    
                    {puedeCrearVerificacion(objetivo) && (
                      <IconButton 
                        size="small" 
                        onClick={() => handleCrearVerificacion(objetivo)}
                        color="primary"
                      >
                        <Add />
                      </IconButton>
                    )}
                    
                    {isAdmin && (
                      <Tooltip title={`Actualizar progreso (${objetivo.tipo_seguimiento})`}>
                        <IconButton
                          size="small"
                          onClick={() => handleActualizarProgreso(objetivo.id, objetivo.tipo_seguimiento)}
                          color={objetivo.tipo_seguimiento === 'automatico' ? 'success' : 'info'}
                        >
                          {getTipoSeguimientoIcon(objetivo.tipo_seguimiento)}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            
            {objetivos.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron objetivos
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para ver detalle del objetivo */}
      <Dialog open={detalleDialog} onClose={() => setDetalleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assessment />
            <Typography variant="h6">Detalle del Objetivo</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedObjetivo && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {selectedObjetivo.objetivo_descripcion}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Responsable:</Typography>
                  <Typography>{selectedObjetivo.responsable.nombre}</Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Meta:</Typography>
                  <Typography>{selectedObjetivo.meta_cuantitativa || 'No definida'}</Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Estado:</Typography>
                  <Chip
                    label={selectedObjetivo.estado}
                    color={getEstadoColor(selectedObjetivo.estado)}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Progreso:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedObjetivo.progreso_porcentaje} 
                      sx={{ flex: 1 }}
                    />
                    <Typography>{selectedObjetivo.progreso_porcentaje}%</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>Verificaciones</Typography>
              
              {verificaciones[selectedObjetivo.id] && verificaciones[selectedObjetivo.id].length > 0 ? (
                verificaciones[selectedObjetivo.id].map((verificacion, index) => (
                  <Accordion key={verificacion.id}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Typography>
                          {formatFecha(verificacion.fecha_verificacion)}
                        </Typography>
                        <Chip
                          label={verificacion.cumplimiento_calidad}
                          color={getCumplimientoColor(verificacion.cumplimiento_calidad)}
                          size="small"
                        />
                        <Typography variant="body2">
                          {verificacion.progreso_reportado}%
                        </Typography>
                        {verificacion.aprobado && (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Evaluador:</Typography>
                          <Typography>{verificacion.evaluador.nombre}</Typography>
                        </Grid>
                        
                        {verificacion.observaciones_verificacion && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Observaciones:</Typography>
                            <Typography>{verificacion.observaciones_verificacion}</Typography>
                          </Grid>
                        )}
                        
                        {verificacion.acciones_requeridas && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Acciones Requeridas:</Typography>
                            <Typography>{verificacion.acciones_requeridas}</Typography>
                          </Grid>
                        )}
                        
                        {verificacion.siguiente_revision && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Próxima Revisión:</Typography>
                            <Typography>{formatFecha(verificacion.siguiente_revision)}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hay verificaciones registradas
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalleDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear verificación */}
      <Dialog open={verificacionDialog} onClose={() => setVerificacionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Verificación</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Progreso Reportado (%)"
                type="number"
                fullWidth
                value={verificacionForm.progreso_reportado}
                onChange={(e) => setVerificacionForm({
                  ...verificacionForm, 
                  progreso_reportado: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Calidad del Cumplimiento</InputLabel>
                <Select
                  value={verificacionForm.cumplimiento_calidad}
                  label="Calidad del Cumplimiento"
                  onChange={(e) => setVerificacionForm({
                    ...verificacionForm, 
                    cumplimiento_calidad: e.target.value
                  })}
                >
                  <MenuItem value="excelente">Excelente</MenuItem>
                  <MenuItem value="bueno">Bueno</MenuItem>
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="deficiente">Deficiente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Observaciones de la Verificación"
                fullWidth
                multiline
                rows={3}
                value={verificacionForm.observaciones_verificacion}
                onChange={(e) => setVerificacionForm({
                  ...verificacionForm, 
                  observaciones_verificacion: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Acciones Requeridas"
                fullWidth
                multiline
                rows={2}
                value={verificacionForm.acciones_requeridas}
                onChange={(e) => setVerificacionForm({
                  ...verificacionForm, 
                  acciones_requeridas: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Siguiente Revisión"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={verificacionForm.siguiente_revision}
                onChange={(e) => setVerificacionForm({
                  ...verificacionForm, 
                  siguiente_revision: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>¿Aprobar verificación?</InputLabel>
                <Select
                  value={verificacionForm.aprobado}
                  label="¿Aprobar verificación?"
                  onChange={(e) => setVerificacionForm({
                    ...verificacionForm, 
                    aprobado: e.target.value
                  })}
                >
                  <MenuItem value={true}>Sí - Aprobar progreso reportado</MenuItem>
                  <MenuItem value={false}>No - Requiere ajustes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificacionDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmitVerificacion} variant="contained">
            Crear Verificación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VerificacionObjetivos